import { Box, Skeleton, Typography, useTheme } from '@mui/joy';
import {Fragment, useEffect, useMemo, useState } from 'react';
import { Page } from './Page';
import { Divider, LandingPageHeading, NewLandingPageHeading, Section } from '../Display';
import {
	DonorsChapterPreview,
	NationalChapterPreview,
	StateChapterPreview,
	AnalysisChapterPreview
} from '../Chapters/ChapterPreviews';
import { useConfigPage } from '../../providers/ConfigPageProvider';
import openSecrets from '../../assets/os-logo-color.png';
import {useYear} from "../../providers";
import {CHAPTERS} from "../../lib/const";
import {useMediaQuery} from "../../hooks";

export type LandingPageProps = {
	stateViewDisabled?: boolean;
	stateViewDisabledText?: string;
	stateViewDescription?: string;
	stateViewChapterEnabled?: boolean;
	stateViewHref?: string;
	nationalViewDisabled?: boolean;
	nationalViewDisabledText?: string;
	nationalViewDescription?: string;
	nationalViewChapterEnabled?: boolean;
	nationalViewHref?: string;
	donorsViewDisabled?: boolean;
	donorsViewDisabledText?: string;
	donorsViewDescription?: string;
	donorsViewChapterEnabled?: boolean;
	donorsViewHref?: string;
	analysisViewDisabled?: boolean;
	analysisViewDisabledText?: string;
	analysisViewDescription?: string;
	analysisViewChapterEnabled?: boolean;
	analysisViewHref?: string;
	stateViewPosition?: number;
	nationalViewPosition?: number;
	donorsViewPosition?: number;
	analysisViewPosition?: number;
};
// CODE FOR ORIGINAL LANDING PAGE (WITHOUT ANALYSIS TILE) IS COMMENTED OUT BELOW
export const LandingPage = (
	{
		stateViewDisabled = CHAPTERS.state.disabled,
		stateViewDisabledText = CHAPTERS.state.disabledText,
		stateViewDescription = undefined,
		stateViewChapterEnabled = true,
		stateViewHref,
		nationalViewDisabled = CHAPTERS.national.disabled,
		nationalViewDisabledText = CHAPTERS.national.disabledText,
		nationalViewDescription = undefined,
		nationalViewChapterEnabled = true,
		nationalViewHref,
		donorsViewDisabled = CHAPTERS.donors.disabled,
		donorsViewDisabledText = CHAPTERS.donors.disabledText,
		donorsViewDescription = undefined,
		donorsViewChapterEnabled = true,
		donorsViewHref,
		analysisViewDisabled = CHAPTERS.analysis.disabled,
		analysisViewDisabledText = CHAPTERS.analysis.disabledText,
		analysisViewDescription = "A final analysis of campaign contributions from individuals to major party congressional and state candidates",
		analysisViewChapterEnabled = true,
		analysisViewHref,
		stateViewPosition = 0,
		nationalViewPosition = 1,
		donorsViewPosition = 2,
		analysisViewPosition = 3
	}: LandingPageProps
) => {
	const theme = useTheme();
	const { year } = useYear();
	const { isFetching, apiData } = useConfigPage();
	const [show, setShow] = useState(false);

	const matchesMd = useMediaQuery(theme.breakpoints.up('md'));

	useEffect(() => {
		setTimeout(() => {
			setShow(true);
		}, 500);
	}, []);

	const numberOfChapters = useMemo(() => {
		return Number(stateViewChapterEnabled) + Number(nationalViewChapterEnabled) + Number(donorsViewChapterEnabled) + Number(analysisViewChapterEnabled)
	}, [stateViewChapterEnabled, nationalViewChapterEnabled, donorsViewChapterEnabled, analysisViewChapterEnabled])

	const getByPosition = (position: number) => {
		if (position === stateViewPosition) {
			if (stateViewChapterEnabled) {
				return (
					<StateChapterPreview
						disabled={stateViewDisabled}
						disabledText={stateViewDisabledText}
						description={stateViewDescription}
						href={stateViewHref}
						headerSx={numberOfChapters <= 2 ? undefined : {
							fontSize: '1.235rem',
							lineHeight: '1.75rem',
							fontWeight: 'bold'
						}}
						subheaderSx={numberOfChapters <= 2 ? undefined : {
							fontSize: '1.235rem',
							lineHeight: '1.75rem',
							fontWeight: 'normal',
							letterSpacing: '0px'
						}}
						width={numberOfChapters <= 2 && matchesMd ? 341 : 280}
						largeCountry={numberOfChapters <= 2 && matchesMd}
					/>
				)
			}
			return null
		}

		if (position === nationalViewPosition) {
			if (nationalViewChapterEnabled) {
				return (
					<NationalChapterPreview
						disabled={nationalViewDisabled}
						disabledText={nationalViewDisabledText}
						description={nationalViewDescription}
						href={nationalViewHref}
						headerSx={numberOfChapters <= 2 ? undefined : {
							fontSize: '1.235rem',
							lineHeight: '1.75rem',
							fontWeight: 'bold'
						}}
						subheaderSx={numberOfChapters <= 2 ? undefined : {
							fontSize: '1.235rem',
							lineHeight: '1.75rem',
							fontWeight: 'normal',
							letterSpacing: '0px'
						}}
						width={numberOfChapters <= 2 && matchesMd ? 341 : 280}
						largeCountry={numberOfChapters <= 2 && matchesMd}
					/>
				)
			}
			return null
		}

		if (position === donorsViewPosition) {
			if (donorsViewChapterEnabled) {
				return (
					<DonorsChapterPreview
						disabled={donorsViewDisabled}
						disabledText={donorsViewDisabledText}
						description={donorsViewDescription}
						href={donorsViewHref}
						headerSx={numberOfChapters <= 2 ? undefined : {
							fontSize: '1.235rem',
							lineHeight: '1.75rem',
							fontWeight: 'bold'
						}}
						subheaderSx={numberOfChapters <= 2 ? undefined : {
							fontSize: '1.235rem',
							lineHeight: '1.75rem',
							fontWeight: 'normal',
							letterSpacing: '0px'
						}}
						width={numberOfChapters <= 2 && matchesMd ? 341 : 280}
						largeCountry={numberOfChapters <= 2 && matchesMd}
					/>
				)
			}
			return null
		}

		if (position === analysisViewPosition) {
			if (analysisViewChapterEnabled) {
				return (
					<AnalysisChapterPreview
						disabled={analysisViewDisabled}
						disabledText={analysisViewDisabledText}
						description={analysisViewDescription}
						href={analysisViewHref}
						headerSx={numberOfChapters <= 2 ? undefined : {
							fontSize: '1.235rem',
							lineHeight: '1.75rem',
							fontWeight: 'bold'
						}}
						subheaderSx={numberOfChapters <= 2 ? undefined : {
							fontSize: '1.235rem',
							lineHeight: '1.75rem',
							fontWeight: 'normal',
							letterSpacing: '0px'
						}}
						width={numberOfChapters <= 2 && matchesMd ? 341 : 280}
						largeCountry={numberOfChapters <= 2 && matchesMd}
					/>
				)
			}
			return null
		}
	}

	return (
		<Page>
			{numberOfChapters > 3 && (
				<NewLandingPageHeading />
			)}
			{show ? (
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', md: 'row' },
						justifyContent: numberOfChapters <= 3 ? 'flex-start' : 'center',
						flexWrap: 'no-wrap',
						alignItems: { xs: 'center', md: 'stretch' },
						flexGrow: 1,
						width: numberOfChapters <= 3 ? '100%' : 'auto',
						marginTop: '20px'
					}}
				>
					<Box
						sx={{
							paddingTop: { xs: '40px', md: '0px' },
							display: 'flex',
							flexDirection: { xs: 'column', md: 'row' },
							gap: { xs: '30px', md: '20px', lg: numberOfChapters <= 3 ? '20px' : '10px', laptop: numberOfChapters <= 3 ? '37px' : '20px' },
							justifyContent: numberOfChapters <= 3 ? 'flex-start' : 'space-between',
							flexWrap: { xs: 'wrap', lg: 'nowrap' },
							alignItems: { xs: 'center', md: 'stretch' },
							width: numberOfChapters <= 3 ? '100%' : 'auto',
						}}
					>
						{numberOfChapters <= 3 && (
							<LandingPageHeading	/>
						)}
						{Array.from({ length: numberOfChapters }, (_, i) => (
							<Fragment key={i}>
								{getByPosition(i) ?? null}
							</Fragment>
						))}
					</Box>
				</Box>
			) : (
				<Box
					sx={{
						backgroundColor: theme.palette.background.surface,
						height: '100vh'
					}}
				/>
			)}

			<Divider sx={{ width: '100%', marginTop: '60px', marginBottom: '20px' }} />

			{year === 2024 && (
				<>
					<Box sx={{ margin: '0 auto' }}>
						<Typography
							level='body2'
							sx={{ marginTop: '42px', textAlign: 'center' }}
						>
							Data fueled in part by:
						</Typography>
						<a href='https://www.opensecrets.org/' target='__blank'>
							<img src={openSecrets} width={194} alt='Open Secrets logo' />
						</a>
					</Box>
					<Section
						heading='About The Project'
						sx={{
							marginTop: '50px',
							marginX: 'auto',
							paddingX: { xs: '16px', md: 0 }
						}}
					>
						<Typography level='body1'>
							<Skeleton loading={isFetching}>
								<div
									dangerouslySetInnerHTML={{
										__html:
											apiData.data[0]?.attributes?.field_about?.processed.replace(
												'href=',
												'target="_blank" and rel="noopener noreferrer" href='
											) ?? ''
									}}
								/>
							</Skeleton>
						</Typography>
					</Section>
					<Section
						heading='Acknowledgements'
						sx={{
							marginTop: '56px',
							marginX: 'auto',
							paddingX: { xs: '16px', md: 0 }
						}}
					>
						<Typography level='body1'>
							<Skeleton loading={isFetching}>
								<div
									dangerouslySetInnerHTML={{
										__html:
											apiData.data[0]?.attributes?.field_acknowledgements?.processed.replace(
												'href=',
												'target="_blank" and rel="noopener noreferrer" href='
											) ?? ''
									}}
								/>
							</Skeleton>
						</Typography>
					</Section>
				</>
			)}

		</Page>
	);
};
// THIS IS THE ORIGINAL LANDING PAGE. PRESERVING IT HERE IN CASE WE DO A 2026 VERSION
{
	/* <Box
sx={{
	display: 'flex',
	flexDirection: { xs: 'column', md: 'row' },
	justifyContent: 'flex-end',
	flexWrap: 'no-wrap',
	alignItems: { xs: 'center', md: 'stretch' }
}}
>
<LandingPageHeading />
<Box
	sx={{
		paddingTop: { xs: '40px', md: '0px' },
		display: 'flex',
		flexDirection: { xs: 'column', md: 'row' },
		gap: { xs: '30px', md: '20px', lg: '10px', laptop: '20px' },
		justifyContent: { xs: 'flex-end', md: 'flex-start' },
		flexWrap: { xs: 'wrap', lg: 'nowrap' },
		alignItems: { xs: 'center', md: 'stretch' }
	}}
>
	<StateChapterPreview />
	<NationalChapterPreview />
	<DonorsChapterPreview />
</Box>
</Box>
<Section heading='About The Project' sx={{ marginTop: '101px' }}>
<Typography level='body1'>
	<Skeleton loading={isFetching}>
		<div
			dangerouslySetInnerHTML={{
				__html:
					apiData.data[0]?.attributes?.field_about?.processed.replace(
						'href=',
						'target="_blank" and rel="noopener noreferrer" href='
					) ?? ''
			}}
		/>
	</Skeleton>
</Typography>
</Section>
<Section heading='Acknowledgements' sx={{ marginTop: '56px' }}>
<Typography level='body1'>
	<Skeleton loading={isFetching}>
		<div
			dangerouslySetInnerHTML={{
				__html:
					apiData.data[0]?.attributes?.field_acknowledgements?.processed.replace(
						'href=',
						'target="_blank" and rel="noopener noreferrer" href='
					) ?? ''
			}}
		/>
	</Skeleton>
</Typography>
</Section> */
}
